<template>
  <div class="statistics-panel">
    <md-card>
      <md-card-header>
        <div class="md-title">Labour Statistics Summary</div>
        <div class="md-subhead">
          Period: {{ formattedDateRange }} ({{ totalDays }} days)
        </div>
      </md-card-header>

      <md-card-content>
        <div v-if="isLoading" class="loading-state">
          <md-progress-spinner md-mode="indeterminate"></md-progress-spinner>
          <p>Calculating statistics...</p>
        </div>

        <div v-else>
          <!-- Summary Cards -->
          <div class="summary-cards">
            <div class="summary-card">
              <div class="card-icon"><md-icon>people</md-icon></div>
              <div class="card-content">
                <div class="card-value">{{ formatNumber(getTotalLabourers()) }}</div>
                <div class="card-label">Total Labourers (Man-Days)</div>
              </div>
            </div>

            <div class="summary-card">
              <div class="card-icon"><md-icon>schedule</md-icon></div>
              <div class="card-content">
                <div class="card-value">{{ formatHours(getTotalHours()) }}</div>
                <div class="card-label">Total Hours</div>
              </div>
            </div>

            <div class="summary-card">
              <div class="card-icon"><md-icon>attach_money</md-icon></div>
              <div class="card-content">
                <div class="card-value">{{ formatCurrency(getTotalAmount()) }}</div>
                <div class="card-label">Total Amount</div>
              </div>
            </div>
          </div>

          <!-- Day Type Breakdown -->
          <div class="statistics-section">
            <h4>Breakdown by Day Type</h4>
            <md-table>
              <md-table-row>
                <md-table-head>Day Type</md-table-head>
                <md-table-head md-numeric>Days</md-table-head>
                <md-table-head md-numeric>Labourers/Day</md-table-head>
                <md-table-head md-numeric>Total Labourers</md-table-head>
                <md-table-head md-numeric>Total Hours</md-table-head>
                <md-table-head md-numeric>Amount</md-table-head>
              </md-table-row>
              <md-table-row v-for="item in dayTypeBreakdown" :key="item.dayType">
                <md-table-cell>
                  <span :class="getDayTypeClass(item.dayType)">{{ item.dayType }}</span>
                </md-table-cell>
                <md-table-cell md-numeric>{{ item.days }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatNumber(item.labourers_per_day) }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatNumber(item.total_labourers) }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatHours(item.total_hours) }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatCurrency(item.total_amount) }}</md-table-cell>
              </md-table-row>
            </md-table>
          </div>

          <!-- Activity Summary Section (New) -->
          <div class="statistics-section" v-if="getActivityStats().length > 0">
            <div class="section-header">
              <h4>Activity Summary</h4>
              <md-button class="md-icon-button md-primary" @click="toggleActivityDetails" :title="showAllActivityDetails ? 'Collapse All' : 'Expand All'">
                <md-icon>{{ showAllActivityDetails ? 'unfold_less' : 'unfold_more' }}</md-icon>
              </md-button>
            </div>

            <md-table>
              <md-table-row>
                <md-table-head></md-table-head>
                <md-table-head>Activity Name</md-table-head>
                <md-table-head>Work Point</md-table-head>
                <md-table-head md-numeric>Daily Labourers</md-table-head>
                <md-table-head md-numeric>Total Labourers</md-table-head>
                <md-table-head md-numeric>Total Hours</md-table-head>
                <md-table-head md-numeric>Regular Amount</md-table-head>
                <md-table-head md-numeric>Exception Amount</md-table-head>
                <md-table-head md-numeric>Total Amount</md-table-head>
              </md-table-row>

              <template v-for="activity in getActivityStats()">
                <md-table-row :key="activity.key" class="activity-summary-row" @click="toggleActivityDetail(activity.key)">
                  <md-table-cell>
                    <md-icon class="expand-icon">
                      {{ isActivityDetailVisible(activity.key) ? 'expand_more' : 'chevron_right' }}
                    </md-icon>
                  </md-table-cell>
                  <md-table-cell>
                    <strong>{{ activity.activity_name }}</strong>
                  </md-table-cell>
                  <md-table-cell>{{ activity.work_point }}</md-table-cell>
                  <md-table-cell md-numeric>{{ formatNumber(activity.daily_labourers) }}</md-table-cell>
                  <md-table-cell md-numeric>{{ formatNumber(activity.total_labourers) }}</md-table-cell>
                  <md-table-cell md-numeric>{{ formatHours(activity.total_hours) }}</md-table-cell>
                  <md-table-cell md-numeric>{{ formatCurrency(activity.regular_amount) }}</md-table-cell>
                  <md-table-cell md-numeric>{{ formatCurrency(activity.exception_amount) }}</md-table-cell>
                  <md-table-cell md-numeric>{{ formatCurrency(activity.total_amount) }}</md-table-cell>
                </md-table-row>

                <!-- Activity Detail Rows (Expanded) -->
                <template v-if="isActivityDetailVisible(activity.key)">
                  <!-- Shift Breakdown -->
                  <md-table-row class="detail-row" :key="activity.key + '-shift-header'">
                    <md-table-cell colspan="3" class="detail-header">
                      <strong>Shift Breakdown for {{ activity.activity_name }}</strong>
                    </md-table-cell>
                    <md-table-cell md-numeric class="detail-header">Daily</md-table-cell>
                    <md-table-cell md-numeric class="detail-header">Total</md-table-cell>
                    <md-table-cell md-numeric class="detail-header">Hours</md-table-cell>
                    <md-table-cell md-numeric class="detail-header">Regular</md-table-cell>
                    <md-table-cell md-numeric class="detail-header">Exception</md-table-cell>
                    <md-table-cell md-numeric class="detail-header">Total</md-table-cell>
                  </md-table-row>

                  <md-table-row v-for="shift in activity.shifts" :key="activity.key + '-shift-' + shift.shift_id" class="shift-detail-row">
                    <md-table-cell colspan="3">
                      <span class="shift-name-indent">{{ shift.shift_name }}</span>
                    </md-table-cell>
                    <md-table-cell md-numeric>{{ formatNumber(shift.daily_labourers) }}</md-table-cell>
                    <md-table-cell md-numeric>{{ formatNumber(shift.total_labourers) }}</md-table-cell>
                    <md-table-cell md-numeric>{{ formatHours(shift.total_hours) }}</md-table-cell>
                    <md-table-cell md-numeric>{{ formatCurrency(shift.regular_amount) }}</md-table-cell>
                    <md-table-cell md-numeric>{{ formatCurrency(shift.exception_amount) }}</md-table-cell>
                    <md-table-cell md-numeric>{{ formatCurrency(shift.total_amount) }}</md-table-cell>
                  </md-table-row>

                  <!-- Day Type Breakdown for this Activity -->
                  <md-table-row class="detail-row" :key="activity.key + '-daytype-header'">
                    <md-table-cell colspan="9" class="detail-subheader">
                      <strong>Breakdown by Day Type</strong>
                    </md-table-cell>
                  </md-table-row>

                  <md-table-row v-for="dayType in activity.dayTypeBreakdown" :key="activity.key + '-daytype-' + dayType.dayType" class="daytype-detail-row">
                    <md-table-cell colspan="3">
                      <span class="daytype-name-indent" :class="getDayTypeClass(dayType.dayType)">
                        {{ dayType.dayType }}
                        <span class="day-count">({{ dayType.days }} shifts)</span>
                      </span>
                    </md-table-cell>
<!--                    <md-table-cell md-numeric>{{ formatNumber(dayType.labourers_per_day) }}</md-table-cell>-->
                    <md-table-cell md-numeric>-</md-table-cell>
                    <md-table-cell md-numeric>{{ formatNumber(dayType.total_labourers) }}</md-table-cell>
                    <md-table-cell md-numeric>{{ formatHours(dayType.total_hours) }}</md-table-cell>
                    <md-table-cell md-numeric>{{ formatCurrency(dayType.total_amount) }}</md-table-cell>
                    <md-table-cell md-numeric>-</md-table-cell>
                    <md-table-cell md-numeric>{{ formatCurrency(dayType.total_amount) }}</md-table-cell>
                  </md-table-row>

                  <!-- Exceptions for this Activity -->
                  <template v-if="activity.exceptions && activity.exceptions.length > 0">
                    <md-table-row class="detail-row" :key="activity.key + '-exceptions-header'">
                      <md-table-cell colspan="9" class="detail-subheader exception-header">
                        <strong>Exceptions</strong>
                      </md-table-cell>
                    </md-table-row>

                    <md-table-row v-for="exception in activity.exceptions" :key="activity.key + '-exception-' + exception.dayType" class="exception-detail-row">
                      <md-table-cell colspan="3">
                        <span class="exception-name-indent" :class="getDayTypeClass(exception.dayType)">
                          {{ exception.dayType }} Exception
                        </span>
                      </md-table-cell>
                      <md-table-cell md-numeric>{{ formatNumber(exception.labourers_per_day) }}</md-table-cell>
                      <md-table-cell md-numeric>{{ formatNumber(exception.total_labourers) }}</md-table-cell>
                      <md-table-cell md-numeric>{{ formatHours(exception.total_hours) }}</md-table-cell>
                      <md-table-cell md-numeric>-</md-table-cell>
                      <md-table-cell md-numeric>{{ formatCurrency(exception.total_amount) }}</md-table-cell>
                      <md-table-cell md-numeric>{{ formatCurrency(exception.total_amount) }}</md-table-cell>
                    </md-table-row>
                  </template>
                </template>
              </template>
            </md-table>
          </div>

          <!-- Statistics by Shift -->
          <div class="statistics-section" v-if="getShiftStats().length > 0">
            <h4>Shift Summary (Total over {{ totalDays }} days)</h4>
            <md-table>
              <md-table-row>
                <md-table-head>Shift</md-table-head>
                <md-table-head md-numeric>Hours/Shift</md-table-head>
                <md-table-head md-numeric>Daily Labourers</md-table-head>
                <md-table-head md-numeric>Total Labourers</md-table-head>
                <md-table-head md-numeric>Total Hours</md-table-head>
                <md-table-head md-numeric>Amount</md-table-head>
              </md-table-row>
              <md-table-row v-for="shift in getShiftStats()" :key="shift.shift_id">
                <md-table-cell>{{ shift.shift_name }}</md-table-cell>
                <md-table-cell md-numeric>{{ shift.shift_hours }} hrs</md-table-cell>
                <md-table-cell md-numeric>{{ formatNumber(shift.daily_labourers) }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatNumber(shift.labourer_count) }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatHours(shift.total_hours) }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatCurrency(shift.total_amount) }}</md-table-cell>
              </md-table-row>
            </md-table>
          </div>

          <!-- Statistics by Work Point -->
          <div class="statistics-section" v-if="getWorkPointStats().length > 0">
            <h4>Work Point Summary</h4>
            <md-table>
              <md-table-row>
                <md-table-head>Work Point</md-table-head>
                <md-table-head md-numeric>Daily Labourers</md-table-head>
                <md-table-head md-numeric>Total Labourers</md-table-head>
                <md-table-head md-numeric>Total Hours</md-table-head>
                <md-table-head md-numeric>Regular Amount</md-table-head>
                <md-table-head md-numeric>Exception Amount</md-table-head>
                <md-table-head md-numeric>Total Amount</md-table-head>
              </md-table-row>
              <md-table-row v-for="(wp, index) in getWorkPointStats()" :key="index">
                <md-table-cell>{{ wp.work_point_name }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatNumber(wp.daily_labourers) }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatNumber(wp.total_labourers) }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatHours(wp.total_hours) }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatCurrency(wp.regular_amount) }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatCurrency(wp.exception_amount) }}</md-table-cell>
                <md-table-cell md-numeric>{{ formatCurrency(wp.total_amount) }}</md-table-cell>
              </md-table-row>
            </md-table>
          </div>

          <!-- Daily Breakdown Chart -->
          <div class="statistics-section" v-if="dailyBreakdown.length > 0">
            <h4>Daily Labourer Trend</h4>
            <div class="daily-breakdown">
              <div v-for="day in dailyBreakdown" :key="day.date" class="daily-bar">
                <div class="daily-label">{{ formatShortDate(day.date) }}</div>
                <div class="bar-container">
                  <div class="bar" :class="getBarClass(day.dayType)" :style="{ width: getBarWidth(day.statistics.labourer_count) + '%' }">
                    <span class="bar-value">{{ day.statistics.labourer_count }}</span>
                  </div>
                </div>
                <div class="daily-type" :class="getDayTypeClass(day.dayType)">{{ day.dayType }}</div>
              </div>
            </div>
          </div>

          <!-- Export Buttons -->
          <div class="export-section" v-if="getTotalLabourers() > 0">
            <md-button class="md-raised md-primary" @click="exportReport">
              <md-icon>download</md-icon> Export
            </md-button>
            <md-button class="md-raised md-success" @click="exportCSV">
              <md-icon>table_chart</md-icon> Export CSV
            </md-button>
            <md-button class="md-raised md-secondary" @click="printReport">
              <md-icon>print</md-icon> Print Report
            </md-button>
          </div>
        </div>
      </md-card-content>
    </md-card>
  </div>
</template>

<script>
import guidanceStatisticsMixin from '@/mixins/guidance-statistics-mixin';

export default {
  name: 'GuidanceStatisticsPanel',
  mixins: [guidanceStatisticsMixin],
  props: {
    guidance: {
      type: Object,
      required: true,
      default: function() {
        return {};
      }
    },
    workPoints: {
      type: Array,
      required: true,
      default: function() {
        return [];
      }
    }
  },
  data() {
    return {
      isLoading: false,
      visibleActivityDetails: {}, // Track which activities are expanded
      showAllActivityDetails: false
    };
  },
  computed: {
    formattedDateRange: function() {
      if (!this.guidance || !this.guidance.start_date || !this.guidance.end_date) {
        return 'N/A';
      }
      return this.guidance.start_date + ' to ' + this.guidance.end_date;
    },

    totalDays: function() {
      return this.getTotalDays();
    },

    dayTypeBreakdown: function() {
      var stats = this.guidanceStatistics;
      if (!stats || !stats.by_day_type) return [];

      return [
        {
          dayType: 'Weekday',
          days: stats.by_day_type.Weekday.days || 0,
          labourers_per_day: stats.by_day_type.Weekday.days > 0
              ? Math.round(stats.by_day_type.Weekday.labourer_count / stats.by_day_type.Weekday.days)
              : 0,
          total_labourers: stats.by_day_type.Weekday.labourer_count || 0,
          total_hours: stats.by_day_type.Weekday.total_hours || 0,
          total_amount: stats.by_day_type.Weekday.total_amount || 0
        },
        {
          dayType: 'Saturday',
          days: stats.by_day_type.Saturday.days || 0,
          labourers_per_day: stats.by_day_type.Saturday.days > 0
              ? Math.round(stats.by_day_type.Saturday.labourer_count / stats.by_day_type.Saturday.days)
              : 0,
          total_labourers: stats.by_day_type.Saturday.labourer_count || 0,
          total_hours: stats.by_day_type.Saturday.total_hours || 0,
          total_amount: stats.by_day_type.Saturday.total_amount || 0
        },
        {
          dayType: 'Sunday',
          days: stats.by_day_type.Sunday.days || 0,
          labourers_per_day: stats.by_day_type.Sunday.days > 0
              ? Math.round(stats.by_day_type.Sunday.labourer_count / stats.by_day_type.Sunday.days)
              : 0,
          total_labourers: stats.by_day_type.Sunday.labourer_count || 0,
          total_hours: stats.by_day_type.Sunday.total_hours || 0,
          total_amount: stats.by_day_type.Sunday.total_amount || 0
        },
        {
          dayType: 'Holiday',
          days: stats.holiday_count || 0,
          labourers_per_day: stats.holiday_count > 0
              ? Math.round((stats.by_day_type.Holiday ? stats.by_day_type.Holiday.labourer_count : 0) / stats.holiday_count)
              : 0,
          total_labourers: stats.by_day_type.Holiday ? stats.by_day_type.Holiday.labourer_count : 0,
          total_hours: stats.by_day_type.Holiday ? stats.by_day_type.Holiday.total_hours : 0,
          total_amount: stats.by_day_type.Holiday ? stats.by_day_type.Holiday.total_amount : 0
        }
      ];
    },

    dailyBreakdown: function() {
      return this.dailyBreakdownData;
    }
  },
  methods: {
    getTotalLabourers: function() {
      var stats = this.guidanceStatistics;
      return stats && stats.labourer_count ? stats.labourer_count : 0;
    },

    getTotalHours: function() {
      var stats = this.guidanceStatistics;
      return stats && stats.total_hours ? stats.total_hours : 0;
    },

    getTotalAmount: function() {
      var stats = this.guidanceStatistics;
      return stats && stats.total_amount ? stats.total_amount : 0;
    },

    getActivityStats: function() {
      var self = this; // Capture this reference
      var activityStats = {};
      var totalDays = this.totalDays;

      if (!this.workPoints || !Array.isArray(this.workPoints)) return [];

      this.workPoints.forEach(function(workPoint, wpIndex) {
        if (!workPoint.activities) return;

        workPoint.activities.forEach(function(activity, actIndex) {
          var activityId = activity.activity_id;
          var activityKey = activityId + '_' + wpIndex + '_' + actIndex;
          var activityName = activity.activity ? activity.activity.name : 'Unknown Activity';
          var workPointName = workPoint.workplace ? workPoint.workplace.name : 'Unknown';

          if (!activityStats[activityKey]) {
            activityStats[activityKey] = {
              key: activityKey,
              activity_name: activityName,
              work_point: workPointName,
              daily_labourers: 0,
              total_labourers: 0,
              total_hours: 0,
              regular_amount: 0,
              exception_amount: 0,
              total_amount: 0,
              shifts: {},
              exceptions: [],
              dayTypeBreakdown: {}
            };
          }

          // Get date range using the mixin method
          var dateRange = self.getDateRangeWithDayTypes();

          dateRange.forEach(function(dateInfo) {
            var dayType = dateInfo.dayType;
            var hasException = self.hasExceptionForDate(activity, dateInfo.date);
            var exceptionData = hasException ? self.getExceptionForDate(activity, dateInfo.date) : null;
            var activityData = exceptionData || activity;
            var isException = !!exceptionData;
            var effectiveDayType = exceptionData ? exceptionData.day_type : dayType;

            if (activityData && activityData.activity_shifts) {
              activityData.activity_shifts.forEach(function(shiftData) {
                if (!shiftData.shift) return;

                var shiftId = shiftData.shift.id;
                var shiftHours = self.calculateShiftHours(shiftData.shift);
                var labourerCount = parseInt(shiftData.labourer_count) || 0;
                var amountPerLabourer = self.getActivityAmount(activityData, effectiveDayType, isException);

                var totalHours = labourerCount * shiftHours;
                var totalAmount = totalHours * amountPerLabourer;

                // Initialize shift stats
                if (!activityStats[activityKey].shifts[shiftId]) {
                  activityStats[activityKey].shifts[shiftId] = {
                    shift_id: shiftId,
                    shift_name: shiftData.shift.name,
                    shift_hours: shiftHours,
                    daily_labourers: 0,
                    total_labourers: 0,
                    total_hours: 0,
                    regular_amount: 0,
                    exception_amount: 0,
                    total_amount: 0
                  };
                }

                // Track by day type
                if (!activityStats[activityKey].dayTypeBreakdown[effectiveDayType]) {
                  activityStats[activityKey].dayTypeBreakdown[effectiveDayType] = {
                    dayType: effectiveDayType,
                    days: 0,
                    labourers_per_day: 0,
                    total_labourers: 0,
                    total_hours: 0,
                    total_amount: 0
                  };
                }

                activityStats[activityKey].dayTypeBreakdown[effectiveDayType].days++;
                activityStats[activityKey].dayTypeBreakdown[effectiveDayType].total_labourers += labourerCount;
                activityStats[activityKey].dayTypeBreakdown[effectiveDayType].total_hours += totalHours;
                activityStats[activityKey].dayTypeBreakdown[effectiveDayType].total_amount += totalAmount;

                activityStats[activityKey].shifts[shiftId].total_labourers += labourerCount;
                activityStats[activityKey].shifts[shiftId].total_hours += totalHours;

                if (isException) {
                  activityStats[activityKey].exception_amount += totalAmount;
                  activityStats[activityKey].shifts[shiftId].exception_amount += totalAmount;
                } else {
                  activityStats[activityKey].regular_amount += totalAmount;
                  activityStats[activityKey].shifts[shiftId].regular_amount += totalAmount;
                }

                activityStats[activityKey].shifts[shiftId].total_amount += totalAmount;
                activityStats[activityKey].total_labourers += labourerCount;
                activityStats[activityKey].total_hours += totalHours;
                activityStats[activityKey].total_amount += totalAmount;
              });
            }

            // Track exceptions separately for display
            if (hasException && exceptionData && !activityStats[activityKey].exceptions.some(function(e) {
              return e.dayType === exceptionData.day_type;
            })) {
              var exceptionDayType = exceptionData.day_type;
              var exceptionTotalLabourers = 0;
              var exceptionTotalHours = 0;
              var exceptionTotalAmount = 0;

              if (exceptionData.activity_shifts) {
                exceptionData.activity_shifts.forEach(function(shiftData) {
                  if (!shiftData.shift) return;
                  var shiftHours = self.calculateShiftHours(shiftData.shift);
                  var labourerCount = parseInt(shiftData.labourer_count) || 0;
                  var amountPerLabourer = self.getActivityAmount(exceptionData, exceptionDayType, true);
                  var totalHours = labourerCount * shiftHours;
                  var totalAmount = totalHours * amountPerLabourer;

                  exceptionTotalLabourers += labourerCount;
                  exceptionTotalHours += totalHours;
                  exceptionTotalAmount += totalAmount;
                });
              }

              activityStats[activityKey].exceptions.push({
                dayType: exceptionDayType,
                labourers_per_day: exceptionTotalLabourers,
                total_labourers: exceptionTotalLabourers,
                total_hours: exceptionTotalHours,
                total_amount: exceptionTotalAmount
              });
            }
          });

          // Calculate daily averages
          if (totalDays > 0) {
            activityStats[activityKey].daily_labourers = Math.round(activityStats[activityKey].total_labourers / totalDays);
          }

          // Calculate per-day averages for shifts
          for (var shiftId in activityStats[activityKey].shifts) {
            if (totalDays > 0) {
              activityStats[activityKey].shifts[shiftId].daily_labourers = Math.round(
                  activityStats[activityKey].shifts[shiftId].total_labourers / totalDays
              );
            }
          }

          // Calculate per-day averages for day types
          for (var dayType in activityStats[activityKey].dayTypeBreakdown) {
            var dayTypeData = activityStats[activityKey].dayTypeBreakdown[dayType];
            if (dayTypeData.days > 0) {
              dayTypeData.labourers_per_day = Math.round(dayTypeData.total_labourers / dayTypeData.days);
            }
          }

          // Convert shifts object to array
          activityStats[activityKey].shifts = Object.values(activityStats[activityKey].shifts);

          // Convert dayTypeBreakdown object to array
          activityStats[activityKey].dayTypeBreakdown = Object.values(activityStats[activityKey].dayTypeBreakdown);
        });
      });

      return Object.values(activityStats);
    },

    getShiftStats: function() {
      var stats = this.shiftStatistics;
      if (!stats) return [];

      var totalDays = this.totalDays;
      var self = this;

      return Object.keys(stats).map(function(key) {
        var shift = stats[key];
        return {
          shift_id: key,
          shift_name: shift.shift_name || 'Unknown',
          shift_hours: shift.shift_hours || 0,
          daily_labourers: totalDays > 0 ? Math.round((shift.labourer_count || 0) / totalDays) : 0,
          labourer_count: shift.labourer_count || 0,
          total_hours: shift.total_hours || 0,
          total_amount: shift.total_amount || 0
        };
      });
    },

    getWorkPointStats: function() {
      var stats = this.workPointStatistics;
      if (!stats) return [];

      var totalDays = this.totalDays;
      var self = this;

      return Object.keys(stats).map(function(key) {
        var wp = stats[key];
        var totalLabourers = wp.total_labourers || 0;
        return {
          work_point_name: wp.work_point_name || 'Unknown',
          daily_labourers: totalDays > 0 ? Math.round(totalLabourers / totalDays) : 0,
          total_labourers: totalLabourers,
          total_hours: wp.total_hours || 0,
          regular_amount: wp.regular_amount || 0,
          exception_amount: wp.exception_amount || 0,
          total_amount: wp.total_amount || 0
        };
      });
    },

    isActivityDetailVisible: function(activityKey) {
      return this.visibleActivityDetails[activityKey] === true;
    },

    toggleActivityDetail: function(activityKey) {
      this.$set(this.visibleActivityDetails, activityKey, !this.visibleActivityDetails[activityKey]);
    },

    toggleActivityDetails: function() {
      this.showAllActivityDetails = !this.showAllActivityDetails;
      var self = this;
      var activityStats = this.getActivityStats();
      var newValue = this.showAllActivityDetails;

      activityStats.forEach(function(activity) {
        self.$set(self.visibleActivityDetails, activity.key, newValue);
      });
    },

    getDayTypeClass: function(dayType) {
      var classes = {
        'Weekday': 'weekday-badge',
        'Saturday': 'weekend-badge',
        'Sunday': 'weekend-badge',
        'Holiday': 'holiday-badge'
      };
      return classes[dayType] || '';
    },

    getBarClass: function(dayType) {
      var classes = {
        'Weekday': 'bar-weekday',
        'Saturday': 'bar-weekend',
        'Sunday': 'bar-weekend',
        'Holiday': 'bar-holiday'
      };
      return classes[dayType] || 'bar-weekday';
    },

    getBarWidth: function(labourerCount) {
      var maxLabourers = this.getMaxDailyLabourers();
      if (maxLabourers === 0) return 0;
      return (labourerCount / maxLabourers) * 100;
    },

    getMaxDailyLabourers: function() {
      var max = 0;
      this.dailyBreakdown.forEach(function(day) {
        if (day.statistics.labourer_count > max) {
          max = day.statistics.labourer_count;
        }
      });
      return max;
    },

    formatShortDate: function(dateString) {
      var date = new Date(dateString);
      return date.getMonth() + 1 + '/' + date.getDate();
    },

    formatCurrency: function(amount) {
      if (amount === undefined || amount === null) return 'TZS 0';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'TZS',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount);
    },

    formatHours: function(hours) {
      if (hours === undefined || hours === null) return '0 hrs';
      return hours.toFixed(1) + ' hrs';
    },

    formatNumber: function(number) {
      if (number === undefined || number === null) return '0';
      return new Intl.NumberFormat('en-US').format(number);
    },

    exportReport: function() {
      this.notifyInfo('Comming Soon')
      return;
      // var report = this.generateSummaryReport();
      // var dataStr = JSON.stringify(report, null, 2);
      // var dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
      // var exportFileDefaultName = 'guidance_stats_' + (this.guidance.guidance_number || 'report') + '_' + Date.now() + '.json';
      //
      // var linkElement = document.createElement('a');
      // linkElement.setAttribute('href', dataUri);
      // linkElement.setAttribute('download', exportFileDefaultName);
      // linkElement.click();
    },

    exportCSV: function() {
      this.notifyInfo('Comming Soon')
      return;
      // var activityStats = this.getActivityStats();
      // var csvRows = [];
      //
      // // Headers
      // csvRows.push([
      //   'Activity Name',
      //   'Work Point',
      //   'Daily Labourers',
      //   'Total Labourers',
      //   'Total Hours',
      //   'Regular Amount',
      //   'Exception Amount',
      //   'Total Amount'
      // ]);
      //
      // // Data rows
      // activityStats.forEach(function(activity) {
      //   csvRows.push([
      //     '"' + activity.activity_name + '"',
      //     '"' + activity.work_point + '"',
      //     activity.daily_labourers,
      //     activity.total_labourers,
      //     activity.total_hours.toFixed(1),
      //     this.formatCurrency(activity.regular_amount),
      //     this.formatCurrency(activity.exception_amount),
      //     this.formatCurrency(activity.total_amount)
      //   ]);
      // }.bind(this));
      //
      // var csvContent = csvRows.map(function(row) {
      //   return row.join(',');
      // }).join('\n');
      //
      // var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      // var link = document.createElement('a');
      // var url = URL.createObjectURL(blob);
      // link.setAttribute('href', url);
      // link.setAttribute('download', 'activity_summary_' + (this.guidance.guidance_number || 'report') + '.csv');
      // link.style.visibility = 'hidden';
      // document.body.appendChild(link);
      // link.click();
      // document.body.removeChild(link);
    },

    printReport: function() {
      this.notifyInfo('Comming Soon')
      return;
      // window.print();
    }
  }
};
</script>

<style scoped>
/* Add styles for activity summary */
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.activity-summary-row {
  cursor: pointer;
  transition: background-color 0.2s;
}

.activity-summary-row:hover {
  background-color: #f5f5f5;
}

.expand-icon {
  cursor: pointer;
  color: #9e9e9e;
}

.detail-row {
  background-color: #fafafa;
}

.detail-header {
  background-color: #eeeeee;
  font-weight: bold;
}

.detail-subheader {
  background-color: #e0e0e0;
  padding-top: 12px;
  padding-bottom: 12px;
}

.shift-detail-row td,
.daytype-detail-row td,
.exception-detail-row td {
  background-color: #fafafa;
}

.shift-name-indent {
  margin-left: 30px;
  display: inline-block;
  font-size: 13px;
}

.daytype-name-indent {
  margin-left: 30px;
  display: inline-block;
  font-size: 13px;
}

.exception-name-indent {
  margin-left: 30px;
  display: inline-block;
  font-size: 13px;
  font-weight: 500;
}

.day-count {
  font-size: 11px;
  color: #666;
  margin-left: 5px;
}

.exception-header {
  background-color: #fff3e0;
}

.exception-detail-row td {
  background-color: #fff8e1;
}

/* Day type badges */
.weekday-badge {
  color: #4caf50;
  font-weight: bold;
}

.weekend-badge {
  color: #ff9800;
  font-weight: bold;
}

.holiday-badge {
  color: #f44336;
  font-weight: bold;
}

/* Bar colors */
.bar-weekday {
  background: linear-gradient(90deg, #4caf50, #81c784);
}

.bar-weekend {
  background: linear-gradient(90deg, #ff9800, #ffb74d);
}

.bar-holiday {
  background: linear-gradient(90deg, #f44336, #ef5350);
}

/* Daily breakdown styles */
.daily-breakdown {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 10px;
}

.daily-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
  font-size: 12px;
}

.daily-label {
  width: 60px;
  font-size: 11px;
}

.bar-container {
  flex: 1;
  background-color: #f5f5f5;
  border-radius: 4px;
  overflow: hidden;
}

.bar {
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding-right: 8px;
  color: white;
  font-size: 11px;
  transition: width 0.3s ease;
}

.daily-type {
  width: 80px;
  font-size: 11px;
  font-weight: 500;
}

/* Keep existing styles */
.statistics-panel {
  margin: 20px;
}

.loading-state {
  text-align: center;
  padding: 40px;
}

.summary-cards {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.summary-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 10px;
  padding: 20px;
  display: flex;
  align-items: center;
  color: white;
  box-shadow: 0 4px 6px rgba(0,0,0,0.1);
}

.summary-card .card-icon {
  margin-right: 15px;
}

.summary-card .card-icon md-icon {
  font-size: 40px;
  width: 40px;
  height: 40px;
  color: white;
}

.summary-card .card-content {
  flex: 1;
}

.summary-card .card-value {
  font-size: 24px;
  font-weight: bold;
}

.summary-card .card-label {
  font-size: 12px;
  opacity: 0.9;
  margin-top: 5px;
}

.statistics-section {
  margin-top: 30px;
}

.statistics-section h4 {
  margin-bottom: 15px;
  color: #333;
}

.export-section {
  margin-top: 30px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

@media print {
  .export-section {
    display: none;
  }

  .summary-card {
    break-inside: avoid;
  }

  .statistics-section {
    break-inside: avoid;
  }

  .activity-summary-row {
    break-inside: avoid;
  }
}
</style>